import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { Icon, habitGlyph } from './icons/Icon';
import type { Habit } from '../types';
interface HabitItemProps {
  habit: Habit;
  completed: boolean;
  /** Omit to render a status-only row (completion driven elsewhere). */
  onToggle?: () => void;
  /** Overrides the static goal line (e.g. the personalized hydration target). */
  goal?: string;
}
export function HabitItem({
  habit,
  completed,
  onToggle,
  goal
}: HabitItemProps) {
  const {
    t
  } = useLocale();
  const interactive = Boolean(onToggle);
  /** Optional examples line (e.g. Mind: "Meditação · leitura · música"). Flexible, never required. */
  const examplesKey = `habit.${habit.id}.examples`;
  const examples = t(examplesKey);
  const hasExamples = examples !== examplesKey;
  return <motion.div role={interactive ? 'button' : undefined} tabIndex={interactive ? 0 : undefined} onClick={onToggle} onKeyDown={interactive ? event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle?.();
    }
  } : undefined} aria-pressed={interactive ? completed : undefined} whileTap={interactive ? {
    scale: 0.985
  } : undefined} transition={{
    duration: 0.14,
    ease: [0.23, 1, 0.32, 1]
  }} className={`flex w-full items-center gap-2 rounded-card border px-2 py-2 text-left transition-[background-color,border-color] duration-150 ease-brand ${interactive ? 'cursor-pointer' : ''} ${completed ? 'border-gold/45 bg-sand-deep/70' : `border-sand-line bg-white/70 ${interactive ? 'hover:border-deep/25' : ''}`}`} data-id="element-772" data-mp="216q8c5qr9kmz">
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-[13px] transition-colors duration-150 ease-brand ${completed ? 'bg-deep text-gold' : 'bg-sand-deep text-soft'}`} data-id="element-773" data-mp="2kw6675rg57x">
        <Icon name={habitGlyph[habit.icon]} size="md" data-id="element-774" data-mp="3p3fy79xi8wei" />
      </span>

      <span className="min-w-0 flex-1" data-id="element-775" data-mp="3p4r2dauv4665">
        <span className="block truncate text-[14px] font-medium text-deep" data-id="element-776" data-mp="3b3y3kdxj2ox8">
          {t(`habit.${habit.id}.title`)}
        </span>
        <span className="block truncate text-[11px] text-muted" data-id="element-777" data-mp="3g3kgem9g235i">
          {goal ?? t(`habit.${habit.id}.goal`)}
        </span>
        {!goal && hasExamples && <span className="block truncate text-[11px] text-soft" data-id="element-778" data-mp="bob7k4xbbdou">{examples}</span>}
      </span>

      <span className="flex shrink-0 items-center gap-2" data-id="element-779" data-mp="3u32anh5ie8m9">
        <span className={`bee-num text-[11px] font-bold ${completed ? 'text-gold-deep' : 'text-muted'}`} data-id="element-780" data-mp="3u0g3vjqfzx53">
          {habit.xp} {t('common.xp')}
        </span>
        <span className={`grid h-6 w-6 place-items-center rounded-pill border transition-colors duration-150 ease-brand ${completed ? 'border-gold bg-gold text-deep' : 'border-deep/25'}`} data-id="element-781" data-mp="qjqw5uf4ihdu">
          <AnimatePresence initial={false} data-id="element-782" data-mp="10q0l09qouust">
            {completed && <motion.span initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.14,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid place-items-center" data-id="element-783" data-mp="10nxsnj22cpkx">
                <CheckIcon size={14} strokeWidth={3} data-id="element-784" data-mp="hregekvyqlk5" />
              </motion.span>}
          </AnimatePresence>
        </span>
      </span>
    </motion.div>;
}